import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useActiveCommissionPlan } from '@/hooks/finance/useCommissionPlans';
import { calculateCommission, type CommissionCalculationResult } from '@/hooks/finance/useCommissionCalculation';
import { differenceInDays, parseISO, isSameMonth } from 'date-fns';
import { getDealSalesAmount } from '@/utils/salesAmount';

export interface PaymentDetail {
  consultantId: string;
  clientName: string;
  paidAt: string;
  paymentType: string;
  amount: number;
  isRecurring: boolean;
}

export interface ConsultantCommissionRow {
  consultantId: string;
  consultantName: string;
  goalAmount: number;
  totalSales: number;
  cashInNew: number;
  cashInRecurring: number;
  totalCashIn: number;
  result: CommissionCalculationResult;
}

export function useCommissionReport(year: number, month: number) {
  const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
  const { data: plan, isLoading: planLoading } = useActiveCommissionPlan(yearMonth, 'us');

  const startDate = `${yearMonth}-01`;
  const endDate = month === 12
    ? `${year + 1}-01-01`
    : `${year}-${String(month + 1).padStart(2, '0')}-01`;

  const query = useQuery({
    queryKey: ['commission-report', yearMonth],
    queryFn: async () => {
      // 1. Goals for US consultants this month
      const { data: goals, error: goalsErr } = await supabase
        .from('user_sales_goals')
        .select('user_id, goal_amount')
        .eq('year', year)
        .eq('month', month)
        .eq('business_unit', 'us');

      if (goalsErr) throw goalsErr;
      if (!goals?.length) return [];

      const userIds = goals.map(g => g.user_id);

      // Determine the earliest plan date for historical deals (recurring cash-in)
      const { data: earliestPlan } = await supabase
        .from('commission_plans')
        .select('effective_from')
        .eq('business_unit', 'us')
        .order('effective_from', { ascending: true })
        .limit(1)
        .single();

      const historicalStart = earliestPlan?.effective_from
        ? `${earliestPlan.effective_from}-01`
        : startDate;

      // 2. Fetch user names, current-month deals, historical deals, payments, employees in parallel
      const [usersRes, dealsRes, historicalDealsRes, paymentsRes, employeesRes] = await Promise.all([
        supabase
          .from('app_users')
          .select('id, display_name, first_name, last_name')
          .in('id', userIds),
        // Current month deals (for sales calculation)
        supabase
          .from('deals')
          .select('id, sales_consultant, total_amount, upsell_credit_amount, deal_status, cancelled_at, closed_date')
          .eq('stage', 'won')
          .eq('business_unit', 'us')
          .gte('closed_date', startDate)
          .lt('closed_date', endDate)
          .in('sales_consultant', userIds),
        // Historical deals (for recurring cash-in mapping) — closed before this month but after plan start
        supabase
          .from('deals')
          .select('id, sales_consultant, closed_date')
          .eq('stage', 'won')
          .eq('business_unit', 'us')
          .gte('closed_date', historicalStart)
          .lt('closed_date', startDate)
          .in('sales_consultant', userIds),
        supabase
          .from('payments')
          .select('id, deal_id, amount, paid_at, payment_type')
          .eq('status', 'paid')
          .gte('paid_at', startDate)
          .lt('paid_at', endDate),
        supabase
          .from('employees')
          .select('app_user_id, hire_date')
          .in('app_user_id', userIds),
      ]);

      if (usersRes.error) throw usersRes.error;
      if (dealsRes.error) throw dealsRes.error;
      if (historicalDealsRes.error) throw historicalDealsRes.error;
      if (paymentsRes.error) throw paymentsRes.error;
      if (employeesRes.error) throw employeesRes.error;

      const users = usersRes.data || [];
      const deals = dealsRes.data || [];
      const historicalDeals = historicalDealsRes.data || [];
      const payments = paymentsRes.data || [];
      const employees = employeesRes.data || [];

      // Map current-month deal_id → consultant (for sales + new cash-in)
      const dealToConsultant = new Map<string, string>();
      const salesByConsultant = new Map<string, number>();
      const currentMonthDealIds = new Set<string>();

      deals.forEach(d => {
        if (!d.sales_consultant) return;
        const salesAmount = getDealSalesAmount({
          deal_status: d.deal_status,
          closed_date: d.closed_date,
          cancelled_at: d.cancelled_at,
          total_amount: d.total_amount,
          amount_received: 0,
          upsell_credit_amount: d.upsell_credit_amount,
        });
        if (salesAmount > 0) {
          dealToConsultant.set(d.id, d.sales_consultant);
          currentMonthDealIds.add(d.id);
        }
        salesByConsultant.set(d.sales_consultant, (salesByConsultant.get(d.sales_consultant) || 0) + salesAmount);
      });

      // Map historical deal_id → consultant (for recurring cash-in)
      const historicalDealToConsultant = new Map<string, string>();
      historicalDeals.forEach(d => {
        if (d.sales_consultant) {
          historicalDealToConsultant.set(d.id, d.sales_consultant);
        }
      });

      // Aggregate cash-in per consultant, split by new vs recurring
      const cashInNewByConsultant = new Map<string, number>();
      const cashInRecurringByConsultant = new Map<string, number>();

      // Collect all deal_ids from payments for contact lookup
      const paymentDealIds = [...new Set(payments.map(p => p.deal_id).filter(Boolean))];

      // Fetch deals with contact info for payment details
      const { data: dealContacts } = paymentDealIds.length > 0
        ? await supabase
            .from('deals')
            .select('id, contact_id')
            .in('id', paymentDealIds)
        : { data: [] as { id: string; contact_id: string | null }[] };

      // Fetch contact names
      const contactIds = [...new Set((dealContacts || []).map(d => d.contact_id).filter(Boolean))] as string[];
      const { data: contacts } = contactIds.length > 0
        ? await supabase
            .from('contacts')
            .select('id, first_name, last_name')
            .in('id', contactIds)
        : { data: [] as { id: string; first_name: string | null; last_name: string | null }[] };

      const contactNameMap = new Map((contacts || []).map(c => [c.id, `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Sem nome']));
      const dealContactMap = new Map((dealContacts || []).map(d => [d.id, d.contact_id]));

      const paymentDetails: PaymentDetail[] = [];

      payments.forEach(p => {
        const amount = p.amount || 0;
        const contactId = dealContactMap.get(p.deal_id);
        const clientName = contactId ? (contactNameMap.get(contactId) || 'Desconhecido') : 'Desconhecido';
        // Check current month deals first
        const currentConsultant = dealToConsultant.get(p.deal_id);
        if (currentConsultant) {
          cashInNewByConsultant.set(currentConsultant, (cashInNewByConsultant.get(currentConsultant) || 0) + amount);
          paymentDetails.push({
            consultantId: currentConsultant,
            clientName,
            paidAt: p.paid_at || '',
            paymentType: p.payment_type || '-',
            amount,
            isRecurring: false,
          });
          return;
        }
        // Check historical deals
        const historicalConsultant = historicalDealToConsultant.get(p.deal_id);
        if (historicalConsultant) {
          cashInRecurringByConsultant.set(historicalConsultant, (cashInRecurringByConsultant.get(historicalConsultant) || 0) + amount);
          paymentDetails.push({
            consultantId: historicalConsultant,
            clientName,
            paidAt: p.paid_at || '',
            paymentType: p.payment_type || '-',
            amount,
            isRecurring: true,
          });
        }
      });

      // Build hire_date map from employees
      const hireDateMap = new Map<string, string>();
      employees.forEach(e => {
        if (e.app_user_id && e.hire_date) {
          hireDateMap.set(e.app_user_id, e.hire_date);
        }
      });

      // Calculate plan tenure
      const monthStartDate = parseISO(startDate);
      const planStartDate = plan?.effective_from
        ? parseISO(`${plan.effective_from}-01`)
        : monthStartDate;
      const planTenureDays = Math.max(0, differenceInDays(monthStartDate, planStartDate));

      // Build rows
      const nameMap = new Map(users.map(u => [u.id, u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Sem nome']));

      const rows: ConsultantCommissionRow[] = goals.map(g => {
        const totalSales = salesByConsultant.get(g.user_id) || 0;
        const cashInNew = cashInNewByConsultant.get(g.user_id) || 0;
        const cashInRecurring = cashInRecurringByConsultant.get(g.user_id) || 0;
        const totalCashIn = cashInNew + cashInRecurring;

        const hireDate = hireDateMap.get(g.user_id);
        const hireTenureDays = hireDate
          ? Math.max(0, differenceInDays(monthStartDate, parseISO(hireDate)))
          : 999;
        const consultantTenureDays = Math.min(hireTenureDays, planTenureDays);

        const result = calculateCommission({
          plan: plan || null,
          totalSales,
          monthlyGoal: g.goal_amount,
          totalCashIn,
          consultantTenureDays,
        });

        return {
          consultantId: g.user_id,
          consultantName: nameMap.get(g.user_id) || 'Desconhecido',
          goalAmount: g.goal_amount,
          totalSales,
          cashInNew,
          cashInRecurring,
          totalCashIn,
          result,
        };
      });

      rows.sort((a, b) => b.result.commissionAmount - a.result.commissionAmount);
      return { rows, paymentDetails };
    },
    enabled: plan !== undefined,
  });

  const result = query.data as { rows: ConsultantCommissionRow[]; paymentDetails: PaymentDetail[] } | undefined;
  return {
    data: result?.rows || [],
    paymentDetails: result?.paymentDetails || [],
    isLoading: planLoading || query.isLoading,
    error: query.error,
    plan,
  };
}
